import { useEffect, useRef } from 'react';

import { useUser } from '@clerk/react';
import { type InboxEntry, WebSocketEntityType } from '@this+that/models';

import { isQualifyingSuggestionInsert } from '../../../hooks/SuggestionTabAlerts/isQualifyingSuggestionInsert';
import { isSuggestionSoundEnabled } from '../../../hooks/SuggestionTabAlerts/isSuggestionSoundEnabled';
import {
  isSuggestionAudioUnlocked,
  playSuggestionSound,
} from '../../../hooks/SuggestionTabAlerts/suggestionSound';
import {
  applyIncomingSuggestion,
  countUnseen,
  markAllSeen,
  parseSuggestionTabAlertsState,
  type SuggestionTabAlertsState,
  shouldPlaySound,
  stampSound,
} from '../../../hooks/SuggestionTabAlerts/suggestionTabAlertsState';
import {
  buildSuggestionTabAlertsKey,
  readSuggestionTabAlertsState,
  updateSuggestionTabAlertsState,
} from '../../../hooks/SuggestionTabAlerts/suggestionTabAlertsStorage';
import { useFaviconBadge } from '../../../hooks/SuggestionTabAlerts/useFaviconBadge';
import { useUnlockAudioOnInteraction } from '../../../hooks/SuggestionTabAlerts/useUnlockAudioOnInteraction';
import {
  DOM_EVENTS,
  VISIBILITY_STATE,
} from '../../../hooks/usePushNotifications';
import { useSuggestionTabAlertsStore } from '../../../store/SuggestionTabAlerts/useSuggestionTabAlertsStore';
import { WINDOW_FOCUS_EVENT } from '../../../utils/constants/suggestionTabAlerts';
import {
  useWebSocket,
  WebSocketEventType,
} from '../../WebSocket/WebSocketContext';

const STORAGE_EVENT = 'storage';

interface Viewer {
  defaultTaskGroupId: string | null;
  soundEnabled: boolean;
}

const isUserLookingAtThisTab = () =>
  document.visibilityState === VISIBILITY_STATE.VISIBLE && document.hasFocus();

const publishCount = (state: SuggestionTabAlertsState) =>
  useSuggestionTabAlertsStore.getState().setUnseenCount(countUnseen(state));

const markSeenAndSync = async (userId: string) => {
  const state = await updateSuggestionTabAlertsState(userId, (current) => ({
    next: markAllSeen(current),
  }));
  publishCount(state);
};

const handleQualifyingInsert = async (
  userId: string,
  entry: InboxEntry,
  soundEnabled: boolean,
) => {
  const now = Date.now();
  const state = await updateSuggestionTabAlertsState(userId, (current) => {
    const next = applyIncomingSuggestion(current, {
      entryId: entry.id,
      createdAt: entry.createdAt,
      isThisTabVisible: isUserLookingAtThisTab(),
      now,
    });
    if (
      soundEnabled &&
      isSuggestionAudioUnlocked() &&
      shouldPlaySound(next, now)
    ) {
      return { next: stampSound(next, now), effect: playSuggestionSound };
    }
    return { next };
  });
  publishCount(state);
};

const syncOnMount = (userId: string) => {
  if (isUserLookingAtThisTab()) {
    void markSeenAndSync(userId);
    return;
  }
  publishCount(
    readSuggestionTabAlertsState(buildSuggestionTabAlertsKey(userId)),
  );
};

export function useSuggestionTabAlertsSync() {
  const { subscribe } = useWebSocket();
  const { user } = useUser();
  const userId = user?.id;
  const unseenCount = useSuggestionTabAlertsStore((s) => s.unseenCount);

  const viewerRef = useRef<Viewer>({
    defaultTaskGroupId: null,
    soundEnabled: false,
  });
  viewerRef.current = {
    defaultTaskGroupId:
      (user?.unsafeMetadata?.defaultTaskGroupId as string | undefined) ?? null,
    soundEnabled: isSuggestionSoundEnabled(user?.unsafeMetadata),
  };

  useFaviconBadge(unseenCount > 0);
  useUnlockAudioOnInteraction();

  useEffect(() => {
    if (!userId) {
      useSuggestionTabAlertsStore.getState().setUnseenCount(0);
      return;
    }

    syncOnMount(userId);

    const unsubscribe = subscribe<InboxEntry>(
      WebSocketEntityType.InboxEntry,
      WebSocketEventType.INSERT,
      ({ item }) => {
        const { defaultTaskGroupId, soundEnabled } = viewerRef.current;
        if (
          item &&
          isQualifyingSuggestionInsert(item, { userId, defaultTaskGroupId })
        ) {
          void handleQualifyingInsert(userId, item, soundEnabled);
        }
      },
    );

    const handleVisibilityChange = () => {
      if (isUserLookingAtThisTab()) void markSeenAndSync(userId);
    };
    const handleWindowFocus = () => {
      void markSeenAndSync(userId);
    };

    const storageKey = buildSuggestionTabAlertsKey(userId);
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== storageKey) return;
      publishCount(parseSuggestionTabAlertsState(event.newValue));
    };

    document.addEventListener(
      DOM_EVENTS.VISIBILITY_CHANGE,
      handleVisibilityChange,
    );
    window.addEventListener(WINDOW_FOCUS_EVENT, handleWindowFocus);
    window.addEventListener(STORAGE_EVENT, handleStorage);

    return () => {
      unsubscribe();
      document.removeEventListener(
        DOM_EVENTS.VISIBILITY_CHANGE,
        handleVisibilityChange,
      );
      window.removeEventListener(WINDOW_FOCUS_EVENT, handleWindowFocus);
      window.removeEventListener(STORAGE_EVENT, handleStorage);
    };
  }, [userId, subscribe]);
}
